/**
 * GapsInput.js
 * Left/right input panels — text or URL, with URL-detection chip.
 * Pure presentation: all state lives in FindGaps (orchestrator).
 */
import React from 'react';
import {
    Box, Paper, Typography, TextField, Button,
    Avatar, Chip, Tooltip, alpha, useTheme,
} from '@mui/material';
import {
    Compare as CompareIcon,
    TextFields as TextIcon,
    TrendingUp as TrendingUpIcon,
    Link as LinkIcon,
    Warning as WarningIcon,
    AutoFixHigh as MagicIcon,
} from '@mui/icons-material';

// ── Single labelled textarea ─────────────────────────────────────────────────
function ContentField({ label, value, onChange, placeholder, color, icon, disabled, urlFetchAvailable }) {
    const isUrl = /^https?:\/\//.test(value.trim());

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 34, height: 34 }}>
                    {icon}
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
                    {label}
                </Typography>

                {isUrl && urlFetchAvailable && (
                    <Chip
                        icon={<LinkIcon sx={{ fontSize: 12 }} />}
                        label="URL — will fetch"
                        size="small"
                        sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                    />
                )}
                {isUrl && !urlFetchAvailable && (
                    <Tooltip title="Install 'requests' and 'beautifulsoup4' on the backend to enable URL fetching">
                        <Chip
                            icon={<WarningIcon sx={{ fontSize: 12 }} />}
                            label="URL fetch unavailable"
                            size="small"
                            sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                        />
                    </Tooltip>
                )}
            </Box>

            <TextField
                fullWidth multiline rows={6}
                value={value} onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5, fontSize: '0.88rem',
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: color,
                            boxShadow: `0 0 0 3px ${alpha(color, 0.1)}`,
                        },
                    },
                }}
            />
            <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                Paste text or enter a URL (http/https) to auto-fetch
            </Typography>
        </Box>
    );
}

// ── Exported panel ────────────────────────────────────────────────────────────
export default function GapsInput({
    yourContent, onYourChange,
    compContent, onCompChange,
    onSubmit, loading, isValid,
    urlFetchAvailable, card,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', width: '100%', px: { xs: 0, lg: 4 } }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'stretch',
                    gap: { xs: 3, md: 0 },
                    position: 'relative',
                    background: alpha(theme.palette.background.paper, 0.4),
                    backdropFilter: 'blur(20px)',
                    borderRadius: 8,
                    p: { xs: 2, md: 1 },
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.4)' : '0 20px 60px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                }}
            >
                {/* Your Content Section */}
                <Box sx={{ flex: 1, p: { xs: 1, md: 3 } }}>
                    <Paper
                        elevation={0}
                        sx={{
                            ...card,
                            height: '100%',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                        }}
                    >
                        <ContentField
                            label="Your Content"
                            value={yourContent}
                            onChange={onYourChange}
                            placeholder={'Paste your social media post, blog excerpt…\n\nor enter a URL: https://yoursite.com/post'}
                            color="#6366f1"
                            icon={<TextIcon sx={{ fontSize: 17 }} />}
                            disabled={loading}
                            urlFetchAvailable={urlFetchAvailable}
                        />
                    </Paper>
                </Box>

                {/* Vertical Divider / VS Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        width: { xs: '100%', md: '80px' },
                        py: { xs: 2, md: 0 },
                    }}
                >
                    {/* Vertical Line for Desktop */}
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            position: 'absolute',
                            top: '10%',
                            bottom: '10%',
                            width: '1px',
                            left: '50%',
                            background: `linear-gradient(to bottom, transparent, ${theme.palette.divider}, transparent)`,
                        }}
                    />
                    {/* VS Badge */}
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '50%',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            flexShrink: 0,
                            background: isDark ? alpha(theme.palette.background.paper, 0.8) : '#fff',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                background: 'linear-gradient(135deg, #6366f1, #10b981)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: '0.9rem',
                                letterSpacing: 1
                            }}
                        >
                            VS
                        </Typography>
                    </Box>
                </Box>

                {/* Competitor Content Section */}
                <Box sx={{ flex: 1, p: { xs: 1, md: 3 } }}>
                    <Paper
                        elevation={0}
                        sx={{
                            ...card,
                            height: '100%',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                        }}
                    >
                        <ContentField
                            label="Competitor / Top Performer"
                            value={compContent}
                            onChange={onCompChange}
                            placeholder={'Paste a top-performing post or competitor content…\n\nor enter a URL: https://competitor.com/viral-post'}
                            color="#10b981"
                            icon={<TrendingUpIcon sx={{ fontSize: 17 }} />}
                            disabled={loading}
                            urlFetchAvailable={urlFetchAvailable}
                        />
                    </Paper>
                </Box>
            </Box>

            {/* Submit Action Area */}
            <Box sx={{ textAlign: 'center', mt: 6, position: 'relative' }}>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!isValid}
                    size="large"
                    startIcon={
                        loading
                            ? <MagicIcon sx={{ animation: 'spin 2s linear infinite' }} />
                            : <CompareIcon />
                    }
                    sx={{
                        borderRadius: 5, px: 8, py: 2.2,
                        fontWeight: 900, fontSize: '1.1rem',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 10px 30px rgba(99,102,241,0.4)',
                        textTransform: 'none',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5855eb, #7c3aed)',
                            boxShadow: '0 15px 40px rgba(99,102,241,0.5)',
                            transform: 'translateY(-3px) scale(1.02)',
                        },
                        '&:disabled': {
                            background: alpha(theme.palette.text.disabled, 0.1),
                            color: theme.palette.text.disabled,
                            boxShadow: 'none'
                        },
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                >
                    {loading ? 'Crunching Strategic Gaps…' : 'Analyse Gaps'}
                </Button>

                {loading && (
                    <Box sx={{ mt: 3, animation: 'pulse 1.5s infinite ease-in-out' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            AI is comparing cross-platform engagement patterns...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Global Keyframes for Animations */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
            `}} />
        </Box>
    );
}
